/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import AddIcon from '@mui/icons-material/AddCircle';
import LaunchOutlined from '@mui/icons-material/LaunchOutlined';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
} from '@mui/material';
import type { AbsoluteURL, AppURL } from '@poweralpha/common';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import usePANavigate from '../react-components/navigate/usePANavigate';

const titleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexWrap: 'wrap',
  fontSize: 14,
  fontWeight: 500,
};

interface Props {
  children: React.ReactElement;
  titleIcon?: React.ReactNode;
  title: string;
  titleContent?: React.ReactNode;
  actions?: React.ReactNode;
  redirectPath?: AbsoluteURL;
  showAddButton?: boolean;
  titleLink?: boolean;
  customAddCallback?: () => void;
}

function Widget({
  children,
  titleIcon,
  title,
  titleContent,
  redirectPath,
  actions,
  showAddButton,
  titleLink,
  customAddCallback,
}: Readonly<Props>) {
  const { navigate } = usePANavigate();
  const onAddClick = useCallback(() => {
    if (customAddCallback) {
      customAddCallback();
      return;
    }
    if (redirectPath) {
      navigate(`${redirectPath}/new` as AppURL);
    }
  }, [customAddCallback, navigate, redirectPath]);

  const titleRender = (
    <>
      {titleIcon && (
        <Box component="span" sx={{ mt: 1 }}>
          {titleIcon}
        </Box>
      )}
      {title}
      {titleLink && redirectPath && <LaunchOutlined sx={{ fontSize: 16 }} color="info" />}
    </>
  );

  return (
    <Card component={Paper}>
      <CardHeader
        sx={{
          p: 0,
          pt: 1,
          px: 1,
          pl: 2,
          height: 38,
          '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
        }}
        title={
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
          >
            {redirectPath ? (
              <MuiLink component={Link} to={redirectPath} sx={titleSx}>
                {titleRender}
              </MuiLink>
            ) : (
              <Box component="span" sx={titleSx}>
                {titleRender}
              </Box>
            )}
            {titleContent}
          </Stack>
        }
        action={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {actions}
            {showAddButton && (customAddCallback || redirectPath) && (
              <IconButton color="success" onClick={onAddClick} size="small">
                <AddIcon />
              </IconButton>
            )}
          </Box>
        }
      />
      <CardContent
        sx={{
          p: 0.5,
          backgroundColor: 'transparent',
          '&:last-child': { pb: 0.5 },
        }}
      >
        <Divider />
        {children}
      </CardContent>
    </Card>
  );
}

export default Widget;
